import React, {useContext, useEffect} from 'react';
import {Button, Form, message, Tabs} from 'antd';
import {useAtom, useAtomValue} from 'jotai';
import axiosApiInstance from '../../../api/axiosClient';
import {DDEXPageContext} from './DDEXMainPage';
import StepsFormFirstPage, {generatedUPCAtom} from './StepFormFirstPage';
import StepsFormSecondPage from './StepsFormSecondPage';
import StepsFormThirdPage from './StepsFormThirdPage';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import './ReleaseEntryStepForm.css'
import {artistOptionsAtom} from './ddex_atoms';
import {
    artTracksFormAtom,
    combinedFormDataAtom,
    currentStepAtom,
    releaseInfoFormAtom,
    submitFormAtom,
    uploadProgressAtom,
    useArtTracksForm,
    useReleaseInfoForm,
    useSubmitForm
} from './release_form_atoms';

export const checksEnabled = true;
// export const checksEnabled = false;

export {fetchAssetLabelOptions, fetchLabelOptions} from './labelOptions';

// export const fetchArtistOptions = async () =>
//     axiosApiInstance
//         .get<API.MusicDistribution.Artist[]>(`/api/products/artists/`)
//         .then(({ data }) => data)
//         .then((artists) =>
//             artists.map((artist) => ({
//                 label: artist.key_name,
//                 value: artist.key_name
//             }))
//         )
//         .catch((reason) => {
//             console.error(reason);
//             return [];
//         });

type Props = {
    root_page: string;
};

const ReleaseEntryStepForm = ({root_page}: Props) => {
    const {releasesActionRef} = useContext(DDEXPageContext);
    const [currentTab, setCurrentTab] = useAtom(currentStepAtom);
    const [uploadProgress, setUploadProgress] = useAtom(uploadProgressAtom);
    const [generatedUPC, setGeneratedUPC] = useAtom(generatedUPCAtom);
    const navigate = useNavigate();
    const [releaseInfoForm] = Form.useForm();
    const [artTracksForm] = Form.useForm();
    const [submitForm] = Form.useForm();
    const [, setReleaseInfoFormAtom] = useAtom(useReleaseInfoForm);
    const [, setArtTracksFormAtom] = useAtom(useArtTracksForm);
    const [, setSubmitFormAtom] = useAtom(useSubmitForm);
    const [releaseInfoData, setReleaseInfoData] = useAtom(releaseInfoFormAtom);
    const [artTracksData, setArtTracksData] = useAtom(artTracksFormAtom);
    const [submitData, setSubmitData] = useAtom(submitFormAtom);
    const combinedData = useAtomValue(combinedFormDataAtom);
    const artistOptions = useAtomValue(artistOptionsAtom);

    useEffect(() => {
        setReleaseInfoFormAtom(releaseInfoForm);
        setArtTracksFormAtom(artTracksForm);
        setSubmitFormAtom(submitForm);
    }, [releaseInfoForm, artTracksForm, submitForm, setReleaseInfoFormAtom, setArtTracksFormAtom, setSubmitFormAtom]);

    useEffect(() => {
        releaseInfoForm.setFieldsValue(releaseInfoData);
        artTracksForm.setFieldsValue(artTracksData);
        submitForm.setFieldsValue(submitData);
    }, [releaseInfoForm, artTracksForm, submitForm]);

    const handleFirstPageFlip = async (
        main_artists: API.MusicDistribution.Artist[],
        featuring_artists: API.MusicDistribution.Artist[]
    ) => {
        console.log('handleFirstPageFlip: ', main_artists, featuring_artists);
        const tracks = artTracksForm.getFieldValue('art_tracks');
        console.log('tracks: ', tracks);
        artTracksForm.setFieldValue(
            'art_tracks',
            tracks.map((track: API.DDEX.Track) => ({...track, main_artists, featuring_artists}))
        );
        // Update the atom with the new data
        setArtTracksData(artTracksForm.getFieldsValue());
        console.log('end handleFirstPageFlip');
    };

    const handleFileResourcesUpload = async () => {
        const uploadPromises = [];

        const coverArtFile = releaseInfoForm.getFieldValue('cover_art_file');
        console.log('ca: ', coverArtFile);
        const uploadCoverArt = async (file) => {
            try {
                const response = await axiosApiInstance.post(
                    '/api/products/releases/generate-presigned-url-upload',
                    {
                        fileName: file.name,
                        fileType: file.type,
                        folderName: releaseInfoForm.getFieldValue('id') || releaseInfoForm.getFieldValue('upc_ean')
                    }
                );

                const {presignedUrl} = response.data;

                await axios.put(presignedUrl, file, {
                    headers: {
                        'Content-Type': file.type
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        console.log(`Cover art upload progress: ${percentCompleted}%`);
                        setUploadProgress(uploadProgress + percentCompleted / uploadPromises.length);
                    }
                });

                // message.success('Cover art uploaded successfully');
            } catch (err) {
                console.error('Error uploading cover art:', err);
                message.error('Error uploading cover art');
            }
        };

        const uploadArtTrack = async (file, index) => {
            try {
                const response = await axiosApiInstance.post(
                    '/api/products/releases/generate-presigned-url-upload',
                    {
                        fileName: file.name,
                        fileType: file.type,
                        folderName: releaseInfoForm.getFieldValue('id') || releaseInfoForm.getFieldValue('upc_ean')
                    }
                );

                const {presignedUrl} = response.data;

                await axios.put(presignedUrl, file, {
                    headers: {
                        'Content-Type': file.type
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        console.log(`Art track ${index} upload progress: ${percentCompleted}%`);
                        setUploadProgress(uploadProgress + percentCompleted / uploadPromises.length);
                    }
                });

                artTracksForm.setFieldValue(['art_tracks', index, 'is_uploaded'], true);
                // Update the atom with the new data
                setArtTracksData(artTracksForm.getFieldsValue());
                // message.success(`Art track ${index} uploaded successfully`);
            } catch (err) {
                console.error(`Error uploading art track ${index}:`, err);
                message.error(`Error uploading art track ${index}`);
            }
        };

        const artTracks = artTracksForm.getFieldValue('art_tracks');

        if (coverArtFile && coverArtFile.length > 0) {
            uploadPromises.push(uploadCoverArt(coverArtFile[0].originFileObj));
        }

        artTracks.forEach((track, index) => {
            const audio = track.audio_file;
            // console.log('audio: ', audio);
            if (audio && audio.length > 0) {
                uploadPromises.push(uploadArtTrack(audio[0].originFileObj, index));
            }
        });

        return Promise.all(uploadPromises);
    };

    const handleSubmit = async () => {
        // Validate all forms
        try {
            await releaseInfoForm.validateFields();
            await artTracksForm.validateFields();

            // Combine all form data
            const values = {
                ...releaseInfoForm.getFieldsValue(),
                ...artTracksForm.getFieldsValue(),
                ...submitForm.getFieldsValue()
            };

            console.log('ONFINISH: ', values);
            const formattedValues = {
                ...values,
                artists: values.artists?.map((key_name) => ({key_name})),
                featuring_artists: values.featuring_artists?.map((key_name) => ({id: key_name})),
                art_tracks: values.art_tracks.map((track) => ({
                    ...track,
                    main_artists: track.main_artists?.map((key_name) => ({key_name})),
                    featuring_artists: track.featuring_artists?.map((key_name) => ({key_name})),
                    composers: track.composers?.map((key_name) => ({key_name})),
                    authors: track.authors?.map((key_name) => ({key_name})),
                    remixers: track.remixers?.map((key_name) => ({key_name})),
                    producers: track.producers?.map((key_name) => ({key_name})),
                    arrangers: track.arrangers?.map((key_name) => ({key_name})),
                    publishers: track.publishers?.map((key_name) => ({key_name})),
                    recording_engineers: track.recording_engineers?.map((key_name) => ({key_name})),
                    mixing_engineers: track.mixing_engineers?.map((key_name) => ({key_name})),
                    mastering_engineers: track.mastering_engineers?.map((key_name) => ({key_name})),
                    executive_producers: track.executive_producers?.map((key_name) => ({key_name})),
                }))
            };
            console.log(formattedValues);

            if (!releaseInfoForm.getFieldValue('upc_ean')) {
                message.error('UPC/EAN is required');
                return false;
            }
            await handleFileResourcesUpload();
            message.success(`Resources uploaded successfully`);

            await axiosApiInstance.post(`/api/products/releases/`, formattedValues);
            releasesActionRef?.current?.reload();
            setUploadProgress(0);
            navigate(root_page);
            return true;
        } catch (error) {
            console.error('Validation failed:', error);
            return false;
        }
    };

    const handleTabChange = (activeKey: string) => {
        if (currentTab === 'release-info' && activeKey !== 'release-info' && releaseInfoForm) {
            setReleaseInfoData((prev) => ({...prev, ...releaseInfoForm.getFieldsValue()}));
        }
        setCurrentTab(activeKey);
    };

    const handleReleaseInfoFinish = async (values: any) => {
        setReleaseInfoData(values);
        await handleFirstPageFlip(values.artists, values.featuring_artists);
        setCurrentTab('art-tracks');
    };

    const handleArtTracksFinish = async (values: any) => {
        setArtTracksData(values);
        setCurrentTab('submit');
    };

    return (
        <div className="release-entry-container" style={{width: '100%', maxWidth: 1600, margin: '0 auto'}}>
            <div className="release-entry-header" style={{display: 'flex', justifyContent: 'space-between', marginBottom: 20}}>
                <h2>Create New Release</h2>
                <div>
                    <Button 
                        key="back" 
                        onClick={() => {
                            if (generatedUPC != '') {
                                axiosApiInstance.put(`/api/products/unbind_upc/${generatedUPC}`);
                                setGeneratedUPC('');
                            }
                            navigate(root_page);
                        }}
                        style={{marginRight: 8}}
                    >
                        Cancel
                    </Button>
                    <Button 
                        key="submit" 
                        type="primary" 
                        onClick={handleSubmit}
                        disabled={currentTab !== 'submit'}
                    >
                        Submit
                    </Button>
                    {currentTab === 'release-info' && (
                        <Button 
                            key="next-release-info" 
                            type="primary" 
                            onClick={() => releaseInfoForm.submit()}
                            style={{marginLeft: 8}}
                        >
                            Next
                        </Button>
                    )}
                    {currentTab === 'art-tracks' && (
                        <Button 
                            key="next-art-tracks" 
                            type="primary" 
                            onClick={() => artTracksForm.submit()}
                            style={{marginLeft: 8}}
                        >
                            Next
                        </Button>
                    )}
                </div>
            </div>
            <div style={{marginBottom: 20}}>
                <Tabs 
                    activeKey={currentTab} 
                    onChange={handleTabChange}
                    type="card"
                    items={[
                        {
                            key: 'release-info',
                            label: 'Release Information',
                            children: (
                                <Form
                                    form={releaseInfoForm}
                                    layout='horizontal'
                                    labelAlign='left'
                                    preserve
                                    onFinish={handleReleaseInfoFinish}
                                >
                                    <StepsFormFirstPage
                                        initialValues={{
                                            upc_ean: 12345600000
                                        }}
                                        edit={false}
                                    />
                                </Form>
                            )
                        },
                        {
                            key: 'art-tracks',
                            label: 'Tracks',
                            children: (
                                <Form
                                    form={artTracksForm}
                                    onFinish={handleArtTracksFinish}
                                >
                                    <StepsFormSecondPage edit={false}/>
                                </Form>
                            )
                        },
                        {
                            key: 'submit',
                            label: 'Submit',
                            children: (
                                <Form
                                    form={submitForm}
                                >
                                    <StepsFormThirdPage/>
                                </Form>
                            )
                        }
                    ]}
                />
            </div>
        </div>
    );
};

export default ReleaseEntryStepForm;
